import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmDialog } from '@/components/deleteConfirmDialog';
import { ErrorBoundary } from '@/components/errorBoundary';
import { ArchiveBranchConfirmModal, BranchFormModal } from './components';
import { BranchListPageSkeleton } from './BranchListPageSkeleton';
import { BranchListPageView } from './BranchListPageView';
import { useBranchListPage } from './useBranchListPage';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * `/branches` — the Branches tab: the branch-management view, and the default
 * tab of the Branches shell (an index route, so the bare `/branches` is this
 * screen without a redirect).
 *
 * Owns its data hook (`useBranchListPage`) and an `ErrorBoundary`; the throw
 * lives in the inner Content child because React boundaries don't catch errors
 * thrown by the component that renders them. The form modal renders outside the
 * boundary so a grid render failure can't take it down.
 *
 * Keeps its own `max-w-7xl` cap — the same one `/products`, `/categories` and
 * the rest of the catalog screens carry — but without `mx-auto`, so the table
 * hugs the same left edge as the tab bar above it instead of drifting to the
 * middle of a wide viewport. The shell itself sets no width: the Shifts tab's
 * register wants every pixel it can get.
 */
export const BranchListPage: React.FC = () => {
    const page = useBranchListPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('branches.components.branchTable.errorTitle'),
            errorMessage: t('branches.components.branchTable.errorMessage'),
            errorRetry: t('branches.components.branchTable.errorRetry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full max-w-7xl flex-col gap-8"
            {...getNodeTestIdProp('branch-list-page')}
            id="branch-list-page"
        >
            <ErrorBoundary
                data-testid="branch-list-page-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="branch-list-page-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <BranchListPageContent page={page} />
            </ErrorBoundary>
            <DeleteConfirmDialog
                confirm={page.deleteConfirm}
                descriptionKey="deleteConfirm.branchDescription"
                promptKey="deleteConfirm.branchPrompt"
            />
            <ArchiveBranchConfirmModal confirm={page.archiveConfirm} />
            <BranchFormModal
                error={page.formError}
                initialValues={page.editingInitialValues}
                isOpen={page.isFormOpen}
                isSubmitting={page.isSubmitting}
                mode={page.formMode}
                onClose={page.onCloseForm}
                onSubmit={page.onSubmitBranch}
            />
        </div>
    );
};

type BranchListPageContentProps = {
    page: ReturnType<typeof useBranchListPage>;
};

const BranchListPageContent: React.FC<BranchListPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    if (page.isLoading || page.data === null) {
        return <BranchListPageSkeleton />;
    }

    return (
        <BranchListPageView
            canImportProducts={page.canImportProducts}
            data={page.data}
            onAddBranch={page.onAddBranch}
            onDeleteBranch={page.onDeleteBranch}
            onEditBranch={page.onEditBranch}
            onImportProducts={page.onImportProducts}
            onPageChange={page.onPageChange}
            onPageSizeChange={page.onPageSizeChange}
            includeArchived={page.includeArchived}
            onRequestArchive={page.onRequestArchive}
            onSearchChange={page.onSearchChange}
            onSetArchived={page.onSetArchived}
            onToggleArchived={page.onToggleArchived}
            page={page.page}
            pageSize={page.pageSize}
            search={page.search}
            totalCount={page.totalCount}
            totalPages={page.totalPages}
        />
    );
};
